import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AdminToolGrid, AdminPostTable } from "../components/AdminDashboardParts";

function useAdminPosts(reloadKey) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftCount, setDraftCount] = useState(null);

  useEffect(() => {
    http
      .get("/posts?limit=100")
      .then(({ data }) => setPosts(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
    // Draft count for the dashboard badge (best-effort).
    http
      .get("/admin/draft-posts")
      .then(({ data }) => setDraftCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setDraftCount(0));
  }, [reloadKey]);

  return { posts, loading, draftCount };
}

export default function AdminDashboard() {
  const [reloadKey, setReloadKey] = useState(0);
  const { posts, loading, draftCount } = useAdminPosts(reloadKey);

  const del = useCallback(async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/posts/${id}`);
      toast.success("Post deleted.");
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  return (
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="admin-dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-3">Admin</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">The Editor&apos;s Desk</h1>
          <p className="mt-4 text-[#404e48] max-w-lg font-light">
            Create, refine, and retire posts. All changes appear instantly on the public site.
          </p>
        </div>
        <Link to="/admin/posts/new" className="btn-primary inline-flex items-center gap-2 self-start" data-testid="new-post-button">
          <Plus size={14} /> New Post
        </Link>
      </div>

      <AdminToolGrid draftCount={draftCount} />
      <AdminPostTable posts={posts} loading={loading} onDelete={del} />
    </div>
  );
}
